import { Fragment, useCallback, useEffect, useState } from 'react'
import { CalendarClock, KeyRound, Mail, Plus, Power, RefreshCw, Search, ShieldCheck, ShieldOff, Trash2, X } from 'lucide-react'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import api from '@/services/api'
import TwoFactorPolicyCard from './TwoFactorPolicyCard'
import UserPermissionsDialog from './UserPermissionsDialog'

type AccessState = 'open' | 'scheduled' | 'pending' | 'expired'

// Where a set-password invite stands. Comes from the server's newest invite
// for the account; the link itself is never sent to the browser.
type InviteStatus = 'none' | 'pending' | 'expired' | 'accepted' | 'revoked'

type InviteSendResult = {
  kind: 'invite' | 'resend'
  email: string
  expires_at: string
}

type AdminUser = {
  invite_status?: InviteStatus
  invite_sent_at?: string | null
  // Per-account module limits. null = unrestricted, so the role decides.
  module_access?: string[] | null
  visible_modules?: string[]
  is_super_admin?: boolean
  id: string
  email: string
  name: string
  role: string
  is_active: boolean
  is_trial: boolean
  totp_enabled: boolean
  // A passkey is a second factor too. `second_factor` is none | authenticator |
  // passkey | both, for the weekly access list; counts only, never a secret.
  passkey_count?: number
  second_factor?: 'none' | 'authenticator' | 'passkey' | 'both'
  trial_expires_at: string | null
  last_login_at: string | null
  created_at: string
  access_starts_at: string | null
  access_ends_at: string | null
  access_note: string | null
  access_state: AccessState
}

type GovernedRolesPayload = {
  roles: Record<string, Record<string, string[]>>
  roleLabels: Record<string, string>
}

/** <input type="datetime-local"> wants local wall time; the API speaks UTC ISO. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

function AccessBadge({ user }: { user: AdminUser }) {
  const map: Record<AccessState, { label: string; cls: string }> = {
    open: { label: 'Always', cls: 'text-stone-500' },
    scheduled: { label: 'In window', cls: 'text-emerald-400' },
    pending: { label: 'Not yet open', cls: 'text-amber-400' },
    expired: { label: 'Window closed', cls: 'text-red-400' },
  }
  const m = map[user.access_state] ?? map.open
  const title = [
    user.access_starts_at ? `From ${new Date(user.access_starts_at).toLocaleString()}` : null,
    user.access_ends_at ? `Until ${new Date(user.access_ends_at).toLocaleString()}` : null,
    user.access_note,
  ].filter(Boolean).join(' · ')
  return <span className={`text-[10px] font-medium ${m.cls}`} title={title || undefined}>{m.label}</span>
}

function AccessWindowEditor({
  user, onSaved, onCancel,
}: { user: AdminUser; onSaved: () => void; onCancel: () => void }) {
  const [starts, setStarts] = useState(toLocalInput(user.access_starts_at))
  const [ends, setEnds] = useState(toLocalInput(user.access_ends_at))
  const [note, setNote] = useState(user.access_note ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (clear = false) => {
    setBusy(true)
    setError(null)
    try {
      await api.put(`/admin/users/${user.id}/access-window`, clear
        ? { clear: true }
        : { access_starts_at: fromLocalInput(starts), access_ends_at: fromLocalInput(ends), note: note.trim() || null })
      onSaved()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Could not save the access window.')
    } finally {
      setBusy(false)
    }
  }

  const field = 'bg-black/40 border border-amber-400/20 rounded px-2 py-1 text-[11px] text-stone-200 outline-none focus:border-amber-400/40'

  return (
    <tr>
      <td colSpan={10} className="bg-black/40 px-3 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] text-stone-500 mb-1">Access starts</label>
            <input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} className={field} />
          </div>
          <div>
            <label className="block text-[10px] text-stone-500 mb-1">Access ends</label>
            <input type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} className={field} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] text-stone-500 mb-1">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Monday client test"
                   className={`${field} w-full`} />
          </div>
          <button type="button" disabled={busy} onClick={() => void save()}
                  className="btn-primary text-[11px] px-3 py-1.5 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save window'}
          </button>
          <button type="button" disabled={busy} onClick={() => void save(true)}
                  className="btn-secondary text-[11px] px-3 py-1.5">Clear</button>
          <button type="button" onClick={onCancel} className="text-[11px] text-stone-500 hover:text-stone-300 px-2">Cancel</button>
        </div>
        <p className="mt-2 text-[10px] text-stone-600">
          Leave a bound empty for an open-ended window. Enforced at sign-in and on every request —
          closing a window ends an active session on its next action.
        </p>
        {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
      </td>
    </tr>
  )
}

type CreatedUser = {
  id: string
  email: string
  name: string
  role: string
  password: string | null
  // Set only when the account was created with an emailed invite.
  invite_sent?: boolean
  invite_error?: string | null
}

const INVITE_LABELS: Record<InviteStatus, { label: string; cls: string; hint: string }> = {
  none: { label: '—', cls: 'text-stone-600', hint: 'No invite sent' },
  pending: { label: 'Sent', cls: 'text-amber-400', hint: 'Link emailed and not yet used' },
  expired: { label: 'Expired', cls: 'text-red-400', hint: 'The link lapsed unused — resend to issue a new one' },
  accepted: { label: 'Accepted', cls: 'text-emerald-400', hint: 'They chose their own password' },
  revoked: { label: 'Replaced', cls: 'text-stone-500', hint: 'A password reset replaced what the link would set' },
}

function InviteBadge({ user }: { user: AdminUser }) {
  const m = INVITE_LABELS[user.invite_status ?? 'none'] ?? INVITE_LABELS.none
  const title = user.invite_sent_at ? `${m.hint} · sent ${new Date(user.invite_sent_at).toLocaleString()}` : m.hint
  return <span className={`text-[10px] font-medium ${m.cls}`} title={title}>{m.label}</span>
}

function StatusBadge({ active }: { active: boolean }) {
  return active
    ? <span className="text-[10px] text-emerald-400 font-medium">Active</span>
    : <span className="text-[10px] text-stone-500">Deactivated</span>
}

function formatWhen(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function apiDetail(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

/** One-time credential display — the password is never retrievable again. */
function CredentialNotice({ user, onClose }: { user: CreatedUser; onClose: () => void }) {
  return (
    <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[12px] font-medium text-amber-300">
            Credentials for {user.name} ({user.email})
          </p>
          <p className="mt-1 text-[11px] text-stone-300">
            Password: <span className="font-mono text-stone-100 select-all">{user.password}</span>
          </p>
          <p className="mt-1 text-[10px] text-stone-500">
            Shown once — copy it now. It cannot be retrieved later; issue a new one with Reset password.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-stone-500 hover:text-stone-300" aria-label="Dismiss credentials">
          <X size={14} />
        </button>
      </div>
    </div>
  )
}

/** Inline row editor: type a password, or generate one. */
function PasswordResetEditor({
  user,
  busy,
  onSubmit,
  onCancel,
}: {
  user: AdminUser
  busy: boolean
  onSubmit: (password?: string) => void
  onCancel: () => void
}) {
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)

  // Mirrors app/core/passwords.py. Checked here only to give immediate
  // feedback — the server enforces it regardless of what this component does.
  const tooShort = password.length > 0 && password.length < 12
  const missingClasses =
    password.length >= 12 &&
    !(/[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password))
  const invalid = tooShort || missingClasses

  return (
    <tr className="bg-white/[0.02]">
      <td colSpan={99} className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-stone-400">
            New password for <span className="text-stone-200">{user.email}</span>
          </span>
          <input
            type={reveal ? 'text' : 'password'}
            value={password}
            autoFocus
            autoComplete="new-password"
            placeholder="Type a password, or generate one"
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password && !invalid) onSubmit(password)
              if (e.key === 'Escape') onCancel()
            }}
            className="w-64 rounded border border-stone-700 bg-black/40 px-2 py-1 font-mono text-[12px] text-stone-100 focus:border-amber-400/40 focus:outline-none"
          />
          <button
            type="button"
            className="text-[10px] text-stone-500 hover:text-stone-300"
            onClick={() => setReveal((v) => !v)}
          >
            {reveal ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            className="btn-primary text-[11px] px-2.5 py-1 disabled:opacity-40"
            disabled={busy || !password || invalid}
            onClick={() => onSubmit(password)}
          >
            Set password
          </button>
          <button
            type="button"
            className="btn-secondary text-[11px] px-2.5 py-1 disabled:opacity-40"
            disabled={busy}
            onClick={() => onSubmit(undefined)}
          >
            Generate instead
          </button>
          <button
            type="button"
            className="text-[11px] text-stone-500 hover:text-stone-300"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        {invalid && (
          <p className="mt-1.5 text-[10px] text-red-400">
            {tooShort
              ? 'At least 12 characters.'
              : 'Needs an uppercase letter, a lowercase letter, and a digit.'}
          </p>
        )}
        <p className="mt-1.5 text-[10px] text-stone-600">
          Replaces the current password immediately. Any live session keeps
          working until its token expires — deactivate the account to cut one now.
        </p>
      </td>
    </tr>
  )
}

function CreateUserForm({
  onCreated,
  onCancel,
  roles,
  roleLabels,
  inviteEnabled,
}: {
  onCreated: (u: CreatedUser) => void
  onCancel: () => void
  roles: string[]
  roleLabels: Record<string, string>
  inviteEnabled: boolean
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('analyst')
  // Off by default, every time: emailing someone is a decision, not a habit.
  const [sendInvite, setSendInvite] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.post<CreatedUser>('/admin/users', {
        name: name.trim(), email: email.trim().toLowerCase(), role,
        ...(inviteEnabled && sendInvite ? { send_invite: true } : {}),
      })
      onCreated(res.data)
      setName(''); setEmail(''); setSendInvite(false)
    } catch (e) {
      setError(apiDetail(e, 'Could not create the account.'))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-black/30 border border-amber-400/20 rounded px-2.5 py-1.5 text-[12px] text-stone-200 placeholder:text-stone-600 outline-none focus:border-amber-400/40'

  return (
    <div className="rounded-lg border border-amber-400/15 bg-black/30 px-3 py-3 space-y-2">
      <p className="text-[12px] font-medium text-stone-200">New permanent account</p>
      <p className="text-[10px] text-stone-500">
        For time-boxed access that expires automatically, use the Trial Licenses tab instead.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <input className={inputCls} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        <input className={inputCls} placeholder="email@firm.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((roleKey) => (
            <option key={roleKey} value={roleKey}>{roleLabels[roleKey] ?? roleKey}</option>
          ))}
        </select>
      </div>
      {inviteEnabled && (
        <label className="flex items-start gap-2 text-[11px] text-stone-300">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
          />
          <span>
            Email a set-password link
            <span className="block text-[10px] text-stone-500">
              They choose their own password; none is generated or shown. The link works once and expires.
            </span>
          </span>
        </label>
      )}
      {error && <p className="text-[11px] text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary text-[12px] px-3 py-1.5 disabled:opacity-50"
          disabled={saving || !name.trim() || !email.trim()}
          onClick={() => void submit()}
        >
          {saving ? 'Creating…' : sendInvite && inviteEnabled ? 'Create and email invite' : 'Create account'}
        </button>
        <button type="button" className="btn-secondary text-[12px] px-3 py-1.5" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

export default function AdminUsersSettingsTab() {
  const { localUser } = useLocalAuth()
  // Whether sign-in needs a second factor is a super-admin decision (the server
  // refuses anyone else), so only they are offered the card.
  const canSetTwoFactorPolicy = Boolean(localUser?.is_super_admin)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [credential, setCredential] = useState<CreatedUser | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmPurgeId, setConfirmPurgeId] = useState<string | null>(null)
  const [confirmResetTwoFactorId, setConfirmResetTwoFactorId] = useState<string | null>(null)
  const [windowEditorId, setWindowEditorId] = useState<string | null>(null)
  const [resetEditorId, setResetEditorId] = useState<string | null>(null)
  const [permissionsUserId, setPermissionsUserId] = useState<string | null>(null)
  // Whether the server has invite email switched on (USER_INVITE_EMAIL_ENABLED).
  // Unknown or unreachable reads as off, so the UI never offers what would only
  // be refused.
  const [inviteEnabled, setInviteEnabled] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)

  const [governedRoles, setGovernedRoles] = useState<string[]>(['admin', 'analyst', 'viewer'])
  const [roleLabels, setRoleLabels] = useState<Record<string, string>>({})

  const loadInviteStatus = useCallback(async () => {
    try {
      const res = await api.get<{ enabled: boolean }>('/admin/invite-email/status')
      setInviteEnabled(Boolean(res.data?.enabled))
    } catch {
      setInviteEnabled(false)
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        api.get<AdminUser[]>('/admin/users'),
        api.get<GovernedRolesPayload>('/settings/rbac'),
      ])
      setUsers(usersResponse.data)
      const assignable = Object.keys(rolesResponse.data.roles)
        .filter((role) => role !== 'super_admin')
        .sort((left, right) => left.localeCompare(right))
      setGovernedRoles(assignable)
      setRoleLabels(rolesResponse.data.roleLabels ?? {})
      await loadInviteStatus()
    } catch {
      setError('Unable to load platform users.')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [loadInviteStatus])

  useEffect(() => { void load() }, [load])

  const act = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(apiDetail(e, 'Action failed.'))
    } finally {
      setBusyId(null)
    }
  }

  const setActive = (u: AdminUser) =>
    act(u.id, async () => {
      if (u.is_active) await api.delete(`/admin/users/${u.id}`)
      else await api.patch(`/admin/users/${u.id}`, { is_active: true })
    })

  const purge = (u: AdminUser) =>
    act(u.id, async () => {
      await api.delete(`/admin/users/${u.id}`, { params: { purge: true } })
      setConfirmPurgeId(null)
    })

  // `password` omitted => the server generates one. Supplied => it is set
  // verbatim, so an admin can hand over a credential they chose (e.g. reading
  // it to someone on a call) instead of transcribing 24 random characters.
  const resetPassword = (u: AdminUser, password?: string) =>
    act(u.id, async () => {
      const res = await api.post<CreatedUser>(
        `/admin/users/${u.id}/reset-password`,
        password ? { password } : {},
      )
      setCredential(res.data)
      setResetEditorId(null)
    })

  // Clears the person's second factor (a lost phone): the authenticator AND any
  // passkeys. It also ends the sessions they already hold, so they sign in again
  // with their password. It never reaches a super admin or another data
  // partition: the server refuses both and says why.
  const resetTwoFactor = (u: AdminUser) =>
    act(u.id, async () => {
      setNotice(null)
      await api.post(`/admin/users/${u.id}/reset-2fa`)
      setConfirmResetTwoFactorId(null)
      setNotice({
        tone: 'ok',
        text: `Two-factor reset for ${u.email}: authenticator and passkeys cleared, and their open sessions ended. They sign in again with their password and set up a new authenticator under Settings > Security.`,
      })
    })

  const hasSecondFactor = (u: AdminUser) => u.totp_enabled || (u.passkey_count ?? 0) > 0

  const canResetTwoFactor = (u: AdminUser) =>
    hasSecondFactor(u) && !u.is_super_admin && u.role !== 'super_admin'

  const secondFactorLabel = (u: AdminUser): string => {
    const passkeys = u.passkey_count ?? 0
    const passkeyText = passkeys === 1 ? 'Passkey' : `Passkeys (${passkeys})`
    if (u.totp_enabled && passkeys > 0) return `Authenticator + ${passkeyText.toLowerCase()}`
    if (u.totp_enabled) return 'Enabled'
    if (passkeys > 0) return passkeyText
    return 'Off'
  }

  // First send when nothing has been sent; Resend otherwise. A resend replaces
  // the earlier link — only the newest email works — so the two are different
  // server calls, not one button with a flag.
  const canInvite = (u: AdminUser) =>
    inviteEnabled &&
    !u.is_trial &&
    u.is_active &&
    !u.is_super_admin &&
    u.role !== 'super_admin' &&
    (u.invite_status ?? 'none') !== 'accepted'

  const sendInvite = (u: AdminUser) =>
    act(u.id, async () => {
      setNotice(null)
      const resend = (u.invite_status ?? 'none') !== 'none'
      const res = await api.post<InviteSendResult>(
        `/admin/users/${u.id}/invite${resend ? '/resend' : ''}`,
      )
      setNotice({
        tone: 'ok',
        text:
          `${resend ? 'New invite' : 'Invite'} emailed to ${u.email}. The link works once and expires ` +
          `${new Date(res.data.expires_at).toLocaleString()}.`,
      })
    })

  const setRole = (u: AdminUser, role: string) =>
    act(u.id, async () => {
      await api.patch(`/admin/users/${u.id}`, { role })
    })

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role.toLowerCase().includes(searchTerm.toLowerCase()),
  )
  const permissionsUser = users.find((u) => u.id === permissionsUserId) ?? null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-medium text-stone-100">Platform Users</h3>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary flex items-center gap-1.5 text-[12px] px-3 py-1.5" onClick={() => void load()}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            className="btn-primary flex items-center gap-1.5 text-[12px] px-3 py-1.5"
            onClick={() => setShowCreate((v) => !v)}
          >
            <Plus size={12} /> Create User
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateUserForm
          onCreated={(u) => {
            setCredential(u)
            setShowCreate(false)
            if (u.invite_sent) {
              setNotice({ tone: 'ok', text: `Invite emailed to ${u.email}. They set their own password at the link.` })
            } else if (u.invite_error) {
              setNotice({
                tone: 'warn',
                text: `${u.email} was created, but the invite email failed: ${u.invite_error} Use Resend invite once mail is working.`,
              })
            }
            void load()
          }}
          onCancel={() => setShowCreate(false)}
          roles={governedRoles}
          roleLabels={roleLabels}
          inviteEnabled={inviteEnabled}
        />
      )}

      {credential?.password && (
        <CredentialNotice user={credential} onClose={() => setCredential(null)} />
      )}

      {canSetTwoFactorPolicy ? <TwoFactorPolicyCard /> : null}

      {notice && (
        <div
          role="status"
          className={`flex items-start justify-between gap-3 rounded border px-3 py-2 text-[12px] ${
            notice.tone === 'ok'
              ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300'
              : 'border-amber-400/30 bg-amber-400/[0.06] text-amber-300'
          }`}
        >
          <span>{notice.text}</span>
          <button type="button" className="text-stone-500 hover:text-stone-300" aria-label="Dismiss notice" onClick={() => setNotice(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Permissions live where the person does. Rendered from the row's own
          data so the dialog opens on what the table already shows, and closed
          by reloading so a saved change is visible without a refresh. */}
      {permissionsUser ? (
        <UserPermissionsDialog
          user={{
            id: permissionsUser.id,
            name: permissionsUser.name,
            email: permissionsUser.email,
            role: permissionsUser.role,
            is_super_admin: permissionsUser.is_super_admin ?? permissionsUser.role === 'super_admin',
            module_access: permissionsUser.module_access ?? null,
            visible_modules: permissionsUser.visible_modules ?? [],
          }}
          onClose={() => {
            setPermissionsUserId(null)
            void load()
          }}
        />
      ) : null}

      {error && <div className="rounded border border-red-400/20 bg-red-400/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}

      <div className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/40 px-3 py-1.5">
        <Search size={13} className="text-stone-600" />
        <input
          type="text"
          placeholder="Search by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-transparent text-[12px] text-stone-300 placeholder:text-stone-600 outline-none"
        />
      </div>

      {loading ? (
        <div className="text-[12px] text-stone-500">Loading users…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-8 text-center text-[12px] text-stone-500">
          No users found. Create an account above, or provision time-boxed access from the Trial Licenses tab.
        </div>
      ) : (
        <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-hidden">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Type</th>
                <th>Expires</th>
                <th>Access</th>
                <th>Status</th>
                <th>2FA</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <Fragment key={u.id}>
                <tr>
                  <td className="font-medium text-stone-200">{u.name}</td>
                  <td className="text-stone-400 text-[11px]">{u.email}</td>
                  <td>
                    {!u.is_trial && u.role !== 'super_admin' ? (
                      <select
                        aria-label={`Role for ${u.email}`}
                        className="rounded border border-amber-400/15 bg-black/40 px-1.5 py-1 text-[10px] text-amber-300 outline-none focus:border-amber-400/40"
                        value={u.role}
                        disabled={busyId === u.id}
                        onChange={(event) => void setRole(u, event.target.value)}
                      >
                        {governedRoles.map((roleKey) => (
                          <option key={roleKey} value={roleKey}>{roleLabels[roleKey] ?? roleKey}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="rounded border border-amber-400/15 bg-amber-400/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-amber-300">{roleLabels[u.role] ?? u.role}</span>
                    )}
                  </td>
                  <td className="text-[11px] text-stone-400">{u.is_trial ? 'Time-boxed' : 'Permanent'}</td>
                  <td className="text-[11px] text-stone-500">{formatWhen(u.trial_expires_at)}</td>
                  <td><AccessBadge user={u} /></td>
                  <td>
                    <StatusBadge active={u.is_active} />
                    {/* Beneath Status rather than a column of its own: the table
                        has no width to spare, and an invite is a state of the
                        account, not a separate fact about it. */}
                    {(u.invite_status ?? 'none') !== 'none' && (
                      <div className="whitespace-nowrap text-[10px] text-stone-500">
                        Invite <InviteBadge user={u} />
                      </div>
                    )}
                  </td>
                  <td className="text-[11px] text-stone-400">{secondFactorLabel(u)}</td>
                  <td className="text-[11px] text-stone-500">{formatWhen(u.last_login_at)}</td>
                  <td>
                    {confirmPurgeId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-red-300">Delete permanently?</span>
                        <button
                          type="button"
                          className="text-[10px] text-red-400 hover:text-red-300 font-medium"
                          disabled={busyId === u.id}
                          onClick={() => void purge(u)}
                        >
                          {busyId === u.id ? 'Deleting…' : 'Yes, delete'}
                        </button>
                        <button type="button" className="text-[10px] text-stone-500 hover:text-stone-300" onClick={() => setConfirmPurgeId(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : confirmResetTwoFactorId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-amber-300">
                          Reset two-factor for {u.name}? This clears their authenticator and passkeys and signs them out.
                        </span>
                        <button
                          type="button"
                          className="text-[10px] text-amber-300 hover:text-amber-200 font-medium"
                          disabled={busyId === u.id}
                          onClick={() => void resetTwoFactor(u)}
                        >
                          {busyId === u.id ? 'Resetting…' : 'Yes, reset 2FA'}
                        </button>
                        <button type="button" className="text-[10px] text-stone-500 hover:text-stone-300" onClick={() => setConfirmResetTwoFactorId(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title={u.is_active ? 'Deactivate — revokes access immediately' : 'Reinstate access'}
                          className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                          disabled={busyId === u.id}
                          onClick={() => void setActive(u)}
                        >
                          <Power size={12} />
                        </button>
                        <button
                          type="button"
                          title="Permissions — role and module access"
                          className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                          disabled={busyId === u.id}
                          onClick={() => setPermissionsUserId(u.id)}
                          aria-label={`Permissions for ${u.email}`}
                        >
                          <ShieldCheck size={12} />
                        </button>
                        <button
                          type="button"
                          title="Schedule an access window"
                          className={`hover:text-amber-300 disabled:opacity-40 ${
                            u.access_state === 'open' ? 'text-stone-500' : 'text-amber-400'
                          }`}
                          disabled={busyId === u.id}
                          onClick={() => setWindowEditorId((prev) => (prev === u.id ? null : u.id))}
                        >
                          <CalendarClock size={12} />
                        </button>
                        <button
                          type="button"
                          title="Set or generate a new password"
                          className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                          disabled={busyId === u.id}
                          onClick={() => setResetEditorId((prev) => (prev === u.id ? null : u.id))}
                        >
                          <KeyRound size={12} />
                        </button>
                        {canResetTwoFactor(u) && (
                          <button
                            type="button"
                            title="Reset two-factor — clears the authenticator and passkeys and signs them out"
                            aria-label={`Reset two-factor for ${u.email}`}
                            className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                            disabled={busyId === u.id}
                            onClick={() => setConfirmResetTwoFactorId(u.id)}
                          >
                            <ShieldOff size={12} />
                          </button>
                        )}
                        {canInvite(u) && (
                          <button
                            type="button"
                            title={
                              (u.invite_status ?? 'none') === 'none'
                                ? 'Email a single-use set-password link'
                                : 'Resend invite — replaces the earlier link'
                            }
                            aria-label={
                              (u.invite_status ?? 'none') === 'none'
                                ? `Send invite to ${u.email}`
                                : `Resend invite to ${u.email}`
                            }
                            className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                            disabled={busyId === u.id}
                            onClick={() => void sendInvite(u)}
                          >
                            <Mail size={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          title={u.is_active ? 'Deactivate first, then delete' : 'Delete permanently'}
                          className="text-stone-600 hover:text-red-400 disabled:opacity-30"
                          disabled={busyId === u.id || u.is_active}
                          onClick={() => setConfirmPurgeId(u.id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {windowEditorId === u.id && (
                  <AccessWindowEditor
                    user={u}
                    onSaved={() => { setWindowEditorId(null); void load() }}
                    onCancel={() => setWindowEditorId(null)}
                  />
                )}
                {resetEditorId === u.id && (
                  <PasswordResetEditor
                    user={u}
                    busy={busyId === u.id}
                    onSubmit={(password) => void resetPassword(u, password)}
                    onCancel={() => setResetEditorId(null)}
                  />
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className="flex items-center justify-between text-[10px] text-stone-600">
          <span>Showing {filtered.length} of {users.length} users</span>
          <span>Deactivation revokes any live session on its next request · deletion requires deactivation first</span>
        </div>
      )}
    </div>
  )
}
